import { useState, useEffect, useRef } from 'react';
import { SILENCIO_MS } from '../streaming/activityPolicy';

/**
 * Tiempo mínimo que el indicador de silencio permanece visible una vez encendido.
 *
 * El cierre de una ronda son dos eventos casi simultáneos —el delta con `done:true`
 * y, milisegundos después, el `tool-call-start` que el fork suelta en `flushFinal`—.
 * Sin histéresis, cada vuelta del bucle produciría un parpadeo: encender, apagar 40 ms
 * más tarde, volver a encender. Un indicador que parpadea se lee como un fallo.
 */
export const MIN_VISIBLE_MS = 700;

/**
 * Firma de actividad del buffer: cambia si y solo si ha llegado algo nuevo.
 *
 * Se comparan longitudes y no contenidos porque el texto crece por concatenación y
 * comparar cadenas de 10 KB en cada render sería caro para lo que aporta. `roundDone`
 * entra porque el delta final de una ronda puede no traer texto (`delta: ''`), y ese
 * evento también es actividad.
 */
function firmaDe(buffer) {
  if (!buffer) return null;
  const tools = Array.isArray(buffer.toolCalls) ? buffer.toolCalls.length : 0;
  return `${(buffer.text || '').length}:${(buffer.preToolText || '').length}:${tools}:${buffer.roundDone ? 1 : 0}`;
}

/**
 * useStreamSilence — ¿lleva el turno un rato sin dar señales de vida?
 *
 * Detecta por **flanco, no por intervalo**: un `setTimeout` que se rearma cada vez que
 * cambia la firma de actividad. No hay polling y, en el peor caso, son dos cambios de
 * estado por episodio de silencio (encender y apagar), en vez de un tick por segundo
 * durante los 37 s que puede durar el hueco.
 *
 * El porqué del umbral, y de que haga falta un reloj para esto, está en
 * `activityPolicy.js`.
 *
 * @param {import('../streaming/useStreamingBuffer').StreamingBuffer|null} buffer
 * @param {{ silencioMs?: number, minVisibleMs?: number }} [opciones] — para los tests;
 *   en producción se usan las constantes.
 * @returns {boolean} true mientras no llegue nada desde hace `silencioMs`.
 */
export default function useStreamSilence(buffer, opciones = {}) {
  const { silencioMs = SILENCIO_MS, minVisibleMs = MIN_VISIBLE_MS } = opciones;

  const [silencioso, setSilencioso] = useState(false);
  // Cuándo se encendió, para la histéresis. `null` = apagado.
  const encendidoEnRef = useRef(null);

  const firma = firmaDe(buffer);
  const activo = firma !== null;

  useEffect(() => {
    if (!activo) {
      encendidoEnRef.current = null;
      setSilencioso(false);
      return undefined;
    }

    // Llegó algo (o es el primer render con buffer): se apaga y se rearma la cuenta.
    // Si el indicador lleva menos del mínimo visible, el apagado se difiere — con
    // `minVisibleMs` < `silencioMs`, siempre ocurre antes del próximo encendido.
    const apagar = () => {
      encendidoEnRef.current = null;
      setSilencioso(false);
    };
    const yaVisible = encendidoEnRef.current ? Date.now() - encendidoEnRef.current : Infinity;
    let temporizadorApagado = null;
    if (yaVisible >= minVisibleMs) {
      apagar();
    } else {
      temporizadorApagado = setTimeout(apagar, minVisibleMs - yaVisible);
    }

    const temporizadorSilencio = setTimeout(() => {
      encendidoEnRef.current = Date.now();
      setSilencioso(true);
    }, silencioMs);

    return () => {
      clearTimeout(temporizadorSilencio);
      if (temporizadorApagado) clearTimeout(temporizadorApagado);
    };
  }, [firma, activo, silencioMs, minVisibleMs]);

  return silencioso;
}
